import React, { useContext, useState } from 'react';
import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from '@material-ui/core';
import { Edit, Visibility } from '@material-ui/icons';

import PropTypes from 'prop-types';

import TablePaginationActions from '../../fields/tablePaginationActions';

import { filteredSearch, getComparator, stableSort, usersWithAccessList } from '../../../utils/helpers';
import { defaultRowsPerPage, defaultSortOrder, rowsPerPageOptions } from '../../../utils/constants';

import translations from '../../../languages/i18n';

import { customOptionsTableStyles, tableTitle } from '../../../styles/formElementsStyles';

import { SessionContext } from '../../../utils/context';

export default function ProgramsList({ handleEditProgram, programsData }) {
  const bgColors = customOptionsTableStyles();
  const classesTable = tableTitle();

  const sessionContext = useContext(SessionContext);

  const [order, setOrder] = React.useState(defaultSortOrder); // Indica l'ordre per defecte
  const [orderBy, setOrderBy] = React.useState('name'); // Indica la columna per la que ordenam per defecte
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const programsDataColumns = 5; // Per saber el nombre de propietats de cada objecte

  return (
    <TableContainer component={Paper}>
      <Table aria-label='programs list table'>
        <TableHead>
          <TableRow>
            <TableCell colSpan={programsDataColumns + 1} align='left' padding='none'>
              <Typography variant='h5' component='div' className={classesTable.title}>{translations['_activePrograms']}</Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow className={bgColors.tableRowHeaderBgColor}>
            {/* ATENCIÓ: no pintam la columna _id */}
            <TableCell align='left'>
              <TableSortLabel
                active={orderBy === 'name'}
                direction={orderBy === 'name' ? order : 'asc'}
                onClick={() => handleRequestSort('name')}
              >
                <strong>{translations['_name']}</strong>
              </TableSortLabel>
            </TableCell>
            <TableCell align='left'>
              <TableSortLabel
                active={orderBy === 'sharedId'}
                direction={orderBy === 'sharedId' ? order : 'asc'}
                onClick={() => handleRequestSort('sharedId')}
              >
                <strong>{translations['_sharedId']}</strong>
              </TableSortLabel>
            </TableCell>
            <TableCell align='left'>
              <TableSortLabel
                active={orderBy === 'type'}
                direction={orderBy === 'type' ? order : 'asc'}
                onClick={() => handleRequestSort('type')}
              >
                <strong>{translations['_type']}</strong>
              </TableSortLabel>
            </TableCell>
            <TableCell align='left'><strong>{translations['_accessibleBy']}</strong></TableCell>
            <TableCell align='center' />{/* Columna per al botó d'editar */}
          </TableRow>
          {(rowsPerPage > 0
            // ATENCIÓ: l'ordenació es fa damunt programsData, si no s'ha introduit res al camp de cerca el programsData inclou tot els programes,
            // és a dir, l'ordenació no es fa sobre la "pagina de programes" que es mostra al llistat (estigui filtrada o no)
            ? stableSort(programsData, getComparator(order, orderBy)).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            : stableSort(programsData, getComparator(order, orderBy))
          ).map((data, index) => (
            <TableRow key={data._id} className={index % 2 ? bgColors.tableRowBgColor1 : bgColors.tableRowBgColor2} hover>
              {/* ATENCIÓ: no pintam la columna _id */}
              <TableCell align='left'>{translations[data.sharedId] || data.name}</TableCell>
              <TableCell align='left'>{data.sharedId}</TableCell>
              <TableCell align='left'>{data.type}</TableCell>
              <TableCell align='left'>{data.accessibleBy && usersWithAccessList(data.accessibleBy.clinicians, true)}</TableCell>
              <TableCell align='center'>
                <IconButton onClick={() => handleEditProgram(data._id)}>
                  {sessionContext.role === 'Admin' ? <Edit /> : <Visibility />}
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              colSpan={programsDataColumns + 1}
              count={programsData.length}
              rowsPerPageOptions={rowsPerPageOptions}
              rowsPerPage={rowsPerPage}
              page={page}
              SelectProps={{
                inputProps: { 'aria-label': 'rows per page' },
                native: true,
              }}
              labelDisplayedRows={({ from, to, count }) => from + ' - ' + ((to !== -1 && to) || count) + ' ' + translations['_of'] + ' ' + ((count !== -1 && count) || (translations['_moreThan'] + ' ') + to)}
              labelRowsPerPage={translations['_rowsPerPage']}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
}

// Indica i controla els props que se han de passar al component
ProgramsList.propTypes = {
  handleEditProgram: PropTypes.func.isRequired,
  programsData: PropTypes.array.isRequired,
};
